// src/PosRegister.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { InputRef } from "antd";
import { pushOrderStatus as pushOrderStatusById } from "./services/posQueue";

import {
  Modal,
  Input,
  Button,
  Row,
  Col,
  Typography,
  message,
  Badge,
  Card,
  Space,
  Drawer,
  Empty,
  Select,
  InputNumber,
  Divider,
  DatePicker,
  Switch,
} from "antd";
import {
  LogoutOutlined,
  BulbOutlined,
  BarChartOutlined,
  BulbFilled,
} from "@ant-design/icons";
import { printOrderTicketStyled } from "./pos/print";

import { useAuthDevice } from "./hooks/pos/useAuthDevice";
import {
  useCatalogGrouped,
  Modifier,
  ModifierGroup,
} from "./hooks/pos/useCatalog";
import PinPad from "./components/pos/PinPad";
import CategoryBar from "./components/pos/CategoryBar";
import ProductGrid from "./components/pos/ProductGrid";
import CartSidebar from "./components/pos/CartSidebar";
import ModifierModal from "./components/pos/ModifierModal";
import {
  cashInLocal,
  cashOutLocal,
  printPeriodicReportLocal,
} from "./pos/fiscal";

import {
  createOrder,
  fetchReceipts,
  voidReceipt,
   listNotPaidOrders,
} from "./services/pos";
import {
  fiscalizeLocal,
  printXReportLocal,
  printZReportLocal,
  type SaleItem,
} from "./pos/fiscal";
import { nextOrderCode } from "./pos/orderSeq";

// === lokalni settings ===
import DeviceSettingsModal from "./components/DeviceSettingsModal";
import { useBootstrapSettings } from "./hooks/useBootstrapSettings";
import { getDeviceSettings } from "./services/deviceSettings";
import { startFlushLoop, stopFlushLoop } from "./services/offline";
import api from "./services/api";
 import { startCardSale, autoConfirmOrRejectByInvoice, probeMonry, TerminalEvent, confirmInvoice } from "./services/paymentTerminal";

type Product = Parameters<typeof ProductGrid>[0]["products"][number];
type CartItem = {
  product: Product;
  qty: number;
  modifiers?: Modifier[];
  note?: string;
};

type OrderType = "dinein" | "takeaway" | "delivery" | null;

export default function PosRegister() {
  // ======== THEME ========
  const [theme, setTheme] = useState<string>(() => {
    return localStorage.getItem("pos.theme") || "light";
  });
  useEffect(() => {
    localStorage.setItem("pos.theme", theme);
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("theme-dark");
      root.classList.remove("theme-light");
    } else {
      root.classList.add("theme-light");
      root.classList.remove("theme-dark");
    }
  }, [theme]);
const [monriOpen, setMonriOpen] = useState(false);
const [monriStatus, setMonriStatus] = useState<{ text: string; code?: string; invoice?: string } | null>(null);
const { Title, Text } = Typography;

  const palette =
    theme === "dark"
      ? {
          appBg: "#0b1220",
          headerBg: "rgba(3,7,18,0.85)",
          headerBorder: "rgba(148,163,184,0.2)",
          leftBg: "#44474eff",
          leftTextMuted: "#94a3b8",
          productBackground: "#1e293b",
        }
      : {
          appBg: "#f6f9fc",
          headerBg: "rgba(255,255,255,.85)",
          headerBorder: "#eef0f4",
          leftBg: "#d7d7d7",
          leftTextMuted: "#555",
          productBackground: "#f9fafb",
        };

  // auth + device
  const {
    device,
    deviceUUID,
    setDeviceUUID,
    deviceName,
    setDeviceName,
    deviceModal,
    ensureDevice,
    employee,
    isLoggedIn,
    doLogin,
    doLogout,
  } = useAuthDevice({ autoLoginDeviceOnly: false });
  const canFiscal = employee?.role === "manager" || employee?.role === "admin";

  const [query, setQuery] = useState("");
  const searchRef = useRef<InputRef>(null);

  // otvorene narudžbe
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const shouldOpen = useBootstrapSettings();
  const [isQuickPay, setIsQuickPay] = useState(false);

  // DRAWER Izvještaji
  const [reportsOpen, setReportsOpen] = useState(false);

  // hotkeys (secret settings)
  useEffect(() => {
    const target = [
      "ArrowUp",
      "ArrowUp",
      "ArrowDown",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "ArrowLeft",
      "ArrowRight",
      "s",
      "d",
      "Enter",
    ];
    const buf: string[] = [];
    const onKey = (e: KeyboardEvent) => {
      buf.push(e.key);
      if (buf.length > target.length) buf.shift();
      if (target.every((v, i) => buf[i]?.toLowerCase() === v.toLowerCase())) {
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => setCanEdit(true), []);
  useEffect(() => {
    if (shouldOpen) setOpen(true);
  }, [shouldOpen]);

  // === Plaćanje meta (SAMO za Payment modal) ===
  type OrderPaymentMeta = {
    payment_type: "cash" | "card" | "virman" | "cheque";
    payment_status?: "unpaid" | "partial" | "paid";
    customer_id?: number | null;
  };

  const [payCustomerId, setPayCustomerId] = useState<number | undefined>();
  const [customerOpts, setCustomerOpts] = useState<
    { label: string; value: number }[]
  >([]);
  const [payOpen, setPayOpen] = useState(false);
async function runMonriSaleWithUI(amountCard: number, ecrRef: string): Promise<"APPROVED" | "DECLINED" | "CANCELED" | "ERROR"> {
  setMonriOpen(true);
  setMonriStatus({ text: `Molimo prislonite karticu — ${amountCard.toFixed(2)} KM` });

  const { result } = await startCardSale(amountCard, ecrRef, (ev: TerminalEvent) => {
    if (ev.type === "PROGRESS") {
      const t = ev.code === "3" ? "Molimo potvrdite na terminalu…" :
               ev.code === "4" ? "Čekamo odgovor izdavaoca…" :
               ev.message?.toString() || "Obrada…";
      setMonriStatus({ text: t, code: ev.code });
    }
  });

  // 1001 -> auto confirm/reject prema dogovoru
  if (result.type === "ERROR" && result.code === "1001") {
    const invoice = (result as any).invoice || ecrRef;
    setMonriStatus({ text: "Čeka potvrdu (CONFIRM)…", code: "1001", invoice });
    const conf = await autoConfirmOrRejectByInvoice(invoice, amountCard);
    setMonriOpen(false);
    if (conf.type === "APPROVED") return "APPROVED";
    if ((conf as any).code === "1100" || (conf as any).code === "2003" || (conf as any).code === "4002") return "CANCELED";
    return "DECLINED";
  }

  setMonriOpen(false);
  if (result.type === "APPROVED") return "APPROVED";
  if (result.type === "DECLINED") return "DECLINED";
  if (result.type === "ERROR" && (result.code === "1100" || result.code === "2003" || result.code === "4002")) return "CANCELED";
  if (result.type === "ERROR") return "ERROR";
  return "ERROR";
}
useEffect(() => {
  let on = true;
  (async () => {
    // Ako postoji "Waiting CONFIRM" iz prethodne sesije – riješi odmah
    const pending = localStorage.getItem("monry.pending.invoice");
    if (pending) {
      try {
        const lastNeed = Number(localStorage.getItem("monry.pending.amount") || "0") || undefined;
        if (lastNeed) {
          await autoConfirmOrRejectByInvoice(pending, lastNeed);
        } else {
          await confirmInvoice(pending, true);
        }
      } catch {}
      localStorage.removeItem("monry.pending.invoice");
      localStorage.removeItem("monry.pending.amount");
    }
  })();
  return () => { on = false; };
}, []);

  // device print settings
  const readDevicePrintSettings = async () => {
    try {
      const s = await getDeviceSettings([
        "printer_ip",
        "printer_share",
        "print_after_fiscal",
        "print_order_on_send",
      ]);
      return {
        printerIp: (s["printer_ip"] || "").trim() || undefined,
        printerShare: (s["printer_share"] || "").trim() || undefined,
        shouldPrintAfterFiscal: String(s["print_after_fiscal"] ?? "1") === "1",
        shouldPrintOrderOnSend: String(s["print_order_on_send"] ?? "1") === "1",
      };
    } catch {
      // fallback for dev/browser
      const paf = localStorage.getItem("print_after_fiscal");
      const ip = localStorage.getItem("printer_ip") || "";
      const share = localStorage.getItem("printer_share") || "";
      const pos = localStorage.getItem("print_order_on_send") ?? "1";
      return {
        printerIp: ip || undefined,
        printerShare: share || undefined,
        shouldPrintAfterFiscal: String(paf ?? "1") === "1",
        shouldPrintOrderOnSend: String(pos ?? "1") === "1",
      };
    }
  };

  useEffect(() => {
    const onSlash = (e: KeyboardEvent) => {
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus({ cursor: "end" } as any);
      }
    };
    window.addEventListener("keydown", onSlash);
    return () => window.removeEventListener("keydown", onSlash);
  }, []);

  // Ctrl+Alt+S otvara Settings
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === "s" && canEdit) {
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit]);

  // offline flush
  useEffect(() => {
    (async () => {
      try {
        const s = await getDeviceSettings(["api_base_url", "device_token"]);
        const base = s["api_base_url"] || "https://app.itable.app";
        const token = s["device_token"] || "";
        if (token) startFlushLoop(base, token, 5000);
      } catch {}
    })();
    return () => stopFlushLoop();
  }, []);

  // catalog
  const [activeCat, setActiveCat] = useState<number | undefined>(undefined);
  const filterCat = query.trim() ? null : activeCat ?? null;
  const { categories, products } = useCatalogGrouped(isLoggedIn, filterCat);
  const catMap = useMemo(() => {
    try {
      return new Map(
        (categories as any[]).map((c: any) => [c.id, c.name || c.title || ""])
      );
    } catch {
      return new Map<number, string>();
    }
  }, [categories]);

  // pretraga
  const norm = (s: any) =>
    String(s ?? "")
      .normalize("NFD")
      // @ts-ignore
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();

  const filteredProducts = useMemo(() => {
    const list = (products as any[]) || [];
    const q = norm(query.trim());
    if (!q) return list;

    return list.filter((p: any) => {
      const name = norm(p.name);
      const priceStr = norm(p.price);
      const cat = norm(catMap.get(p.category_id) || "");
      const sku = norm(p.sku || p.code || p.barcode || "");
      return (
        name.includes(q) ||
        cat.includes(q) ||
        sku.includes(q) ||
        priceStr.includes(q)
      );
    });
  }, [products, query, catMap]);

  // ====== CART ======
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("pos.cart") || "[]");
    } catch {
      return [];
    }
  });
  const persist = (next: CartItem[]) =>
    localStorage.setItem("pos.cart", JSON.stringify(next));

  // ORDER TYPE (note)
  const [orderType, setOrderType] = useState<OrderType>(null);
  const orderTypeLabel = (t: OrderType) =>
    t === "takeaway"
      ? "Za ponijeti"
      : t === "delivery"
      ? "Dostava"
      : t === "dinein"
      ? "U objektu"
      : "";

  // istorija fiskalnih
  const [histOpen, setHistOpen] = useState(false);
  const [histRows, setHistRows] = useState<
    Array<{
      id: number;
      number?: string;
      total: number;
      status: string;
      created_at?: string;
    }>
  >([]);

  // modal modifikatora
  const [modOpen, setModOpen] = useState(false);
  const [modProduct, setModProduct] = useState<Product | null>(null);
  const [modGroups, setModGroups] = useState<ModifierGroup[]>([]);
  const [modSelections, setModSelections] = useState<Record<number, number[]>>(
    {}
  );
  const [modNote, setModNote] = useState<string>("");

  const onUpdateNote = (index: number, note: string) => {
    setCart((prev) => {
      const out = prev.map((x, i) => (i === index ? { ...x, note } : x));
      persist(out);
      return out;
    });
  };

  const lineTotal = (i: any) => {
    const base =
      Number(i.unit_price ?? i.price ?? 0) * Number(i.quantity ?? i.qty ?? 0) -
      Number(i.discount ?? 0);
    const mods = Array.isArray(i.modifiers)
      ? i.modifiers.reduce(
          (s: number, m: any) =>
            s + Number(m.price_delta || 0) * Number(m.quantity || 1),
          0
        )
      : 0;
    return base + mods;
  };
  //eslint-disable-next-line
  const orderTotal = (o: any) =>
    (o.items || []).reduce((s: number, it: any) => s + lineTotal(it), 0);

  // Narudžbe / Istorija
  const openOrders = useCallback(async () => {
     refresh();
    setOrdersOpen(true);
  }, []);


    // --- ORDERS REFRESH ---
    async function refresh() {
       try {
        const data = await listNotPaidOrders();
        setOrders(Array.isArray(data) ? data : []);
           //   setOrdersOpen(true);

      } catch (e) {
        // @ts-ignore
        console.error("TVQueue refresh error:", e?.message || e);
      }
    }
  
    useEffect(() => {
      refresh(); // odmah
      const t = setInterval(refresh, 15000); // svake 7s
      return () => clearInterval(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


  const openHistory = useCallback(async () => {
    try {
      const rows = await fetchReceipts(100);
      setHistRows(rows);
      setHistOpen(true);
    } catch {
      message.error("Ne mogu dohvatiti istoriju.");
    }
  }, []);

  // Dodavanje u korpu
  const addToCart = useCallback(
    (p: Product, chosenMods: Modifier[] = [], note?: string) => {
      setCart((prev) => {
        const key = (mods: Modifier[]) =>
          mods
            .map((m) => m.id)
            .sort()
            .join(",");
        const k = key(chosenMods);
        const idx = prev.findIndex(
          (x) => x.product.id === p.id && key(x.modifiers || []) === k
        );
        const out =
          idx >= 0
            ? prev.map((x, i) =>
                i === idx ? { ...x, qty: (x.qty || 0) + 1 } : x
              )
            : [
                ...prev,
                { product: p, qty: 1, modifiers: chosenMods, note: note || "" },
              ];
        persist(out);
        return out;
      });
    },
    []
  );
  const inc = (i: number) =>
    setCart((c) => {
      const out = c.map((x, ix) =>
        ix === i ? { ...x, qty: (x.qty || 0) + 1 } : x
      );
      persist(out);
      return out;
    });
  const dec = (i: number) =>
    setCart((c) => {
      const out = c.flatMap((x, ix) =>
        ix === i
          ? (x.qty || 0) > 1
            ? [{ ...x, qty: (x.qty || 0) - 1 }]
            : []
          : [x]
      );
      persist(out);
      return out;
    });
  const rem = (i: number) =>
    setCart((c) => {
      const out = c.filter((_, ix) => ix !== i);
      persist(out);
      return out;
    });

  const total = useMemo(
    () =>
      cart.reduce((s, it) => {
        const base = Number(it.product.price ?? 0) || 0;
        const mods = (it.modifiers || []).reduce(
          (m, mm) => m + (Number(mm.price_delta) || 0),
          0
        );
        return s + (base + mods) * (Number(it.qty) || 0);
      }, 0),
    [cart]
  );

  // Modifikatori (modal)
  const openModifiers = useCallback(
    (p: Product) => {
      const groups: ModifierGroup[] = (p as any).modifierGroups || [];
      const hasAny = groups?.some((g) => (g.modifiers?.length || 0) > 0);
      if (!hasAny) {
        addToCart(p);
        return;
      }
      const initial: Record<number, number[]> = {};
      groups.forEach((g) => (initial[g.id] = []));
      setModProduct(p);
      setModGroups(groups);
      setModSelections(initial);
      setModOpen(true);
    },
    [addToCart]
  );

  const confirmModifiers = useCallback(() => {
    if (!modProduct) return;
    for (const g of modGroups) {
      const picked = modSelections[g.id] || [];
      const min =
        (g.required ? Math.max(1, g.min_select || 0) : g.min_select || 0) || 0;
      const max = g.max_select || 99;
      if (picked.length < min) {
        message.warning(`Odaberi najmanje ${min} u grupi: ${g.name}`);
        return;
      }
      if (picked.length > max) {
        message.warning(`Maksimalno ${max} u grupi: ${g.name}`);
        return;
      }
    }
    const idx = new Map<number, Modifier>();
    modGroups.forEach((g) => g.modifiers?.forEach((m) => idx.set(m.id, m)));
    const chosen: Modifier[] = [];
    Object.values(modSelections).forEach((ids) =>
      ids.forEach((id) => {
        const m = idx.get(id);
        if (m) chosen.push({ ...m, price_delta: Number(m.price_delta) || 0 });
      })
    );
    addToCart(modProduct, chosen);
    setModOpen(false);
    setModProduct(null);
    setModGroups([]);
    setModSelections({});
  }, [modProduct, modGroups, modSelections, addToCart]);

  //eslint-disable-next-line
  const getOutletId = () => {
    const s = localStorage.getItem("pos.outlet_id");
    const n = s ? Number(s) : NaN;
    if (Number.isFinite(n) && n > 0) return n;
    return (employee as any)?.outlet_id || 1;
  };

  // ====== LOADING & CONFIRM ======
  const [orderLoading, setOrderLoading] = useState(false);

  const [payOrder, setPayOrder] = useState<any>(null);
  const [payLoading, setPayLoading] = useState(false);

  // ====== Create order (bez načina plaćanja; payment isključivo u Payment modalu) ======
  const doCreateOrder = useCallback(
    async (meta?: OrderPaymentMeta) => {
      const outlet_id = getOutletId();
      if (!outlet_id) {
        message.warning("Nije podešen outlet.");
        return;
      }

      const tag = orderTypeLabel(orderType);

      const items = cart.map((c) => {
        const unit_price = Number((c.product as any).price) || 0;
        const quantity = Number(c.qty) || 1;
        const note = (c.note || "").trim();
        const modifiers = (c.modifiers || []).map((m) => ({
          modifier_id: Number(m.id),
          price: Number(m.price_delta) || 0,
        }));
        return {
          product_id: Number(c.product.id),
          quantity,
          unit_price,
          modifiers,
          note,
        };
      });

      const number = await nextOrderCode();
      const payload: any = { outlet_id, number, items };

      if(isQuickPay){
        payload.payment_status = "paid";
        payload.status = "delivered";
      } 

      if (tag) payload.note = `[${tag}]`;

      // ⚠️ payment_type se NE postavlja ovdje — ide kroz Payment modal

      const res = await createOrder(payload);

      try {
        const { printerIp, printerShare, shouldPrintOrderOnSend } =
          await readDevicePrintSettings();
        if (shouldPrintOrderOnSend && (printerIp || printerShare)) {
          const orderNo = res?.number || res?.id;
          const ticketItems = cart.map((c) => ({
            name: c.product.name,
           
            unit_price: Number((c.product as any).price) || 0,
            qty: Number(c.qty || 1),
            modifiers: (c.modifiers || []).map((m) => m.name).filter(Boolean),
            note: c.note ?? null,
          }));

          await printOrderTicketStyled(
            {
              clientName: device?.name || "iTable APP",
              orderId: orderNo,
              workerName: employee?.name || undefined,
              verifyBaseUrl: "https://portal.itable.app/order-check",
            },
            ticketItems,
            { printerIp, printerShare }
          );
        }
      } catch (e) {
        console.error("Greška pri printu narudžbe:", e);
        message.error("Greška pri printu narudžbe.");
      }
      message.success("Narudžba kreirana.");
      setCart([]);
      localStorage.setItem("pos.cart", "[]");
      //eslint-disable-next-line
    },
    //eslint-disable-next-line
    [cart, orderType, getOutletId]
  );

  const confirmCreateOrder = useCallback(() => {
    if (!cart.length) {
      message.warning("Dodaj barem jedan proizvod.");
      return;
    }
    const itemsCount = cart.reduce((s, it) => s + (Number(it.qty) || 0), 0);
    const tag = orderTypeLabel(orderType);
    Modal.confirm({
      title: "Potvrdi slanje u kuhinju",
      content: (
        <div>
          <div>
            Stavki: <b>{itemsCount}</b>
          </div>
          <div>
            Ukupno: <b>{total.toFixed(2)} KM</b>
          </div>
          {tag && (
            <div>
              Tip narudžbe: <b>{tag}</b>
            </div>
          )}
        </div>
      ),
      okText: "Pošalji",
      cancelText: "Odustani",
      onOk: async () => {
        setOrderLoading(true);
        try {
          // 🔹 direktno kreiramo narudžbu BEZ payment polja
          await doCreateOrder();
          setOrderType(null);
        } catch {
          message.error("Greška pri kreiranju narudžbe.");
        } finally {
          setOrderLoading(false);
        }
      },
    });
  }, [cart, total, doCreateOrder, orderType]);

  // ====== Plaćanja (split) ======
  const payTypes = ["Gotovina", "Kartica", "Virman", "Cek"];
  const [payments, setPayments] = useState<
    Array<{ type: string; amount: number }>
  >([]);
  const sumPayments = useMemo(
    () => payments.reduce((s, p) => s + Number(p.amount || 0), 0),
    [payments]
  );

  // mapiranje labela iz postojećeg SELECT-a na BE code
  const typeLabelToCode = useCallback((label: string) => {
    switch (label) {
      case "Gotovina":
        return "cash";
      case "Kartica":
        return "card";
      case "Virman":
        return "virman";
      case "Cek":
        return "cheque";
      default:
        return "cash";
    }
  }, []);

  // da li u splitu učestvuje Virman (sa iznosom > 0)
  const hasVirman = useMemo(
    () =>
      payments.some((p) => p.type === "Virman" && Number(p.amount || 0) > 0),
    [payments]
  );

  // primarni način plaćanja = onaj sa najvećim iznosom
  const primaryMethod = useMemo<"cash" | "card" | "virman" | "cheque">(() => {
    const positives = payments.filter((p) => Number(p.amount || 0) > 0);
    if (!positives.length) return "cash";
    const totals: Record<"cash" | "card" | "virman" | "cheque", number> = {
      cash: 0,
      card: 0,
      virman: 0,
      cheque: 0,
    };
    positives.forEach((p) => {
      const code = typeLabelToCode(p.type) as keyof typeof totals;
      totals[code] += Number(p.amount || 0);
    });
    return (Object.entries(totals) as Array<
      ["cash" | "card" | "virman" | "cheque", number]
    >).sort((a, b) => b[1] - a[1])[0][0];
  }, [payments, typeLabelToCode]);

  const needAmount = useMemo(() => {
    return payOrder
      ? Number(orderTotal(payOrder).toFixed(2))
      : Number(total.toFixed(2));
    //eslint-disable-next-line
  }, [payOrder, total, orderTotal]);

  const [cashGiven, setCashGiven] = useState<number>(0);

  // otvori modal za postojeću narudžbu
  const openPaymentForOrder = (o: any) => {
    const tot = Number(orderTotal(o).toFixed(2));
    setPayOrder(o);
    setPayments([{ type: "Gotovina", amount: tot }]);
    setCashGiven(tot);
    setPayCustomerId(undefined);
    setPayOpen(true);
  };
  // otvori modal za trenutnu korpu
  const openPaymentForCart = () => {
    if (!cart.length) {
      message.warning("Dodaj barem jedan proizvod.");
      return;
    }
    const tot = Number(total.toFixed(2));
    setPayOrder(null);
    setPayments([{ type: "Gotovina", amount: tot }]);
    setCashGiven(tot);
    setPayCustomerId(undefined);
    setPayOpen(true);
  };

  // Fiskalne stavke helperi
  const toFiscalFromOrder = (o: any): SaleItem[] =>
    (o?.items || []).map((it: any) => ({
      name: it.product_name || it.name,
      unit: it.unit || "kom",
      price:
        Number(it.unit_price || 0) +
        (Array.isArray(it.modifiers)
          ? it.modifiers.reduce(
              (s: number, m: any) => s + Number(m.price_delta || 0),
              0
            )
          : 0),
      vat: it.vat || "E",
      qty: Number(it.quantity || it.qty || 1),
      discount: Number(it.discount || 0) || 0,
    }));
  const toFiscalFromCart = (): SaleItem[] =>
    (cart || []).map((c) => {
      const base = Number((c.product as any).price) || 0;
      const mods = (c.modifiers || []).reduce(
        (s, m) => s + Number(m.price_delta || 0),
        0
      );
      return {
        name: c.product.name,
        unit: (c as any).unit || (c.product as any).unit || "kom",
        price: base + mods,
        vat: (c as any).vat || (c.product as any).vat_code || "E",
        qty: Number(c.qty) || 1,
        discount: 0,
      };
    });

  // učitavanje klijenata: aktivira se kad je u splitu Virman
  useEffect(() => {
    if (payOpen && hasVirman) {
      api
        .get("/pos/getcustomers?status=active")
        .then((r) => {
          const arr = (r.data?.data || r.data || []).map((c: any) => ({
            label: c.company_name?.length
              ? `${c.company_name} (${c.name})`
              : c.name,
            value: c.id,
          }));
          setCustomerOpts(arr);
        })
        .catch(() => setCustomerOpts([]));
    }
  }, [payOpen, hasVirman]);

  // izvrši naplatu (sve postavke payment_type + customer_id rade se ovdje)
  const runPay = useCallback(async () => {
    if (hasVirman && !payCustomerId) {
      message.warning("Za virman izaberi klijenta.");
      return;
    }

    if (payOrder) {
      const need = Number(orderTotal(payOrder).toFixed(2));
      const got = Number(sumPayments.toFixed(2));
      if (Math.abs(got - need) > 0.009) {
        message.error("Zbir uplata mora biti jednak ukupnom iznosu.");
        return;
      }

      // upiši vrstu plaćanja na postojeći order (best-effort)
      try {
        await api.post(`/pos/orders/${payOrder.id}/pay`, {
          payment_type: primaryMethod,
          payment_status: "paid",
          customer_id: hasVirman ? payCustomerId : undefined,
        });
      } catch {
        // BE možda nema PATCH — ignoriši
      }

      // fiskalizuj postojeću narudžbu
      const saleItems = toFiscalFromOrder(payOrder);
      const cardAmount = payments
  .filter((p) => typeLabelToCode(p.type) === "card")
  .reduce((s, p) => s + Number(p.amount || 0), 0);

if (cardAmount > 0) {
  // ping terminal – disable ako je offline
  const online = await probeMonry();
  if (!online) {
    message.error("Kartično plaćanje nije dostupno — terminal offline.");
    setPayLoading(false);
    return;
  }
  const ref = String(payOrder?.number || payOrder?.id || await nextOrderCode());
  const outcome = await runMonriSaleWithUI(cardAmount, ref);
  if (outcome !== "APPROVED") {
    if (outcome === "CANCELED") message.warning("Korisnik je otkazao na terminalu.");
    else if (outcome === "DECLINED") message.error("Transakcija odbijena na terminalu.");
    else message.error("Greška na terminalu.");
    setPayLoading(false);
    return;
  }
}
      const fx = await fiscalizeLocal(saleItems, payments);
      if (fx?.vrsta && fx.vrsta !== "OK") {
        message.error(
          fx.values?.OpisGreske ||
            fx.values?.Poruka ||
            "Fiskalni server: greška"
        );
        return;
      }
      const fiscalNumber = fx?.values.BrojFiskalnogRacuna

      // u pripremu
      try {
        if (payOrder?.id)
          await pushOrderStatusById(Number(payOrder.id), isQuickPay ? "delivered" : "in_preparation", fiscalNumber);
      } catch (e) {
        console.warn("pushOrderStatus nije uspio:", e);
      }
      // clean
      setCart([]);
      localStorage.setItem("pos.cart", "[]");
      setPayOpen(false);
      message.success("Fiskalni račun izdan.");
      setIsQuickPay(false);

      
      
    } else {
      // plaćanje iz korpe → FISKAL → CREATE ORDER sa payment info
      if (!cart.length) {
        message.warning("Dodaj barem jedan proizvod.");
        return;
      }
      const need = Number(total.toFixed(2));
      const got = Number(sumPayments.toFixed(2));
      if (Math.abs(got - need) > 0.009) {
        message.error("Zbir uplata mora biti jednak ukupnom iznosu.");
        return;
      }
      const saleItems: SaleItem[] = toFiscalFromCart();
      // --- MONRI (NOVA NARUDŽBA IZ KORPE) ---------------------
const cardAmount2 = payments
  .filter((p) => typeLabelToCode(p.type) === "card")
  .reduce((s, p) => s + Number(p.amount || 0), 0);
        const number = await nextOrderCode();

if (cardAmount2 > 0) {
  const online = await probeMonry();
  if (!online) {
    message.error("Kartično plaćanje nije dostupno — terminal offline.");
    setPayLoading(false);
    return;
  }

  // koristimo 'number' kao f12 referencu
  const outcome = await runMonriSaleWithUI(cardAmount2, number);
  if (outcome !== "APPROVED") {
    if (outcome === "CANCELED") message.warning("Korisnik je otkazao na terminalu.");
    else if (outcome === "DECLINED") message.error("Transakcija odbijena na terminalu.");
    else message.error("Greška na terminalu.");
    setPayLoading(false);
    return;
  }
}
// --------------------------------------------------------

      const fx = await fiscalizeLocal(saleItems, payments);
      if (fx?.vrsta && fx.vrsta !== "OK") {
        message.error(
          fx.values?.OpisGreske ||
            fx.values?.Poruka ||
            "Fiskalni server: greška"
        );
        return;
      }

      // kreiraj ORDER na BE + upiši payment_type/customer_id
      const outlet_id = getOutletId();
      const tag = orderTypeLabel(orderType);
      //broj fiskalnog računa
      const fiscalNumber = fx?.values.BrojFiskalnogRacuna
      const itemsForOrder = cart.map((c) => {
        const unit_price = Number((c.product as any).price) || 0;
        const quantity = Number(c.qty) || 1;
        const modifiers = (c.modifiers || []).map((m) => ({
          modifier_id: Number(m.id),
          price: Number(m.price_delta) || 0,
        }));
        return {
          product_id: Number(c.product.id),
          quantity,
          unit_price,
          note: (c.note || "").trim(),
          modifiers,
        };
      });
      const payload: any = {
        outlet_id,
        number,
        fiscal_nimber : fiscalNumber,
        items: itemsForOrder,
        payment_type: primaryMethod,
        payment_status: "paid",
      };
      if (tag) payload.note = `[${tag}]`;
      if (hasVirman && payCustomerId) payload.customer_id = payCustomerId;

       if(isQuickPay){
        payload.payment_status = "paid";
        payload.status = "delivered";
      } 

      const res: any = await createOrder(payload);

      // u pripremu
      try {
        const newId = Number(res?.id);
        if (Number.isFinite(newId) && newId > 0) {
          await pushOrderStatusById(newId, isQuickPay ? "delivered" : "in_preparation", fiscalNumber);
        }
      } catch (e) {
        console.warn("pushOrderStatus (new) nije uspio:", e);
      }

      try {
        const { printerIp, printerShare, shouldPrintOrderOnSend } =
          await readDevicePrintSettings();
        if (shouldPrintOrderOnSend && (printerIp || printerShare)) {
          const orderNo = res?.number || res?.id;
          const ticketItems = cart.map((c) => ({
            name: c.product.name,
            qty: Number(c.qty || 1),
            modifiers: (c.modifiers || []).map((m) => m.name).filter(Boolean),
            note: c.note ?? null,
          }));

          await printOrderTicketStyled(
            {
              clientName: device?.name || "iTable APP",
              orderId: orderNo,
              workerName: employee?.name || undefined,
              verifyBaseUrl: "https://portal.itable.app/order-check",
            },
            ticketItems,
            { printerIp, printerShare }
          );
        }
      } catch (e) {
        console.error("Greška pri printu narudžbe:", e);
        message.error("Greška pri printu narudžbe.");
      }

      setCart([]);
      localStorage.setItem("pos.cart", "[]");
      setPayOpen(false);
      message.success("Fiskalni račun izdan.");
      setIsQuickPay(false);

    }
    //eslint-disable-next-line
  }, [
    payOrder,
    cart,
    total,
    payments,
    sumPayments,
    orderType,
    getOutletId,
    primaryMethod,
    hasVirman,
    payCustomerId,
  ]);

  const setMethodForChecked = (checked: boolean) => {
    console.log("setMethodForChecked", checked);
    setIsQuickPay(checked);
    
  }

  // Potvrda prije naplate + loading
  const confirmAndRunPay = useCallback(() => {
    if (hasVirman && !payCustomerId) {
          message.warning("Za virman odaberi klijenta.");
          return;
        }
    Modal.confirm({
      title: "Potvrdi naplatu",
      content: (
        <div>
          Iznos: <b>{needAmount.toFixed(2)} KM</b>
          <br />
          Način:{" "}
          {payments
            .map((p) => `${p.type} ${Number(p.amount || 0).toFixed(2)} KM`)
            .join(" + ") || "—"}
          <br />
          Brza naplata:{" "}
          <Switch style={{ marginTop: 8 }} onChange={setMethodForChecked} />
        </div>
      ),
      okText: "Naplati",
      cancelText: "Odustani",
      onOk: async () => {
        console.log("confirmAndRunPay", {
          needAmount,
          payments,
          sumPayments,
          primaryMethod,
          hasVirman,
          payCustomerId,
        });

        if (hasVirman && !payCustomerId) {
          message.warning("Za virman odaberi klijenta.");
          return;
        }
        setPayLoading(true);
        try {
          await runPay();
        } catch (e: any) {
          message.error(e?.message || "Naplata nije uspjela.");
        } finally {
          setPayLoading(false);
        }
      },
    });
    //eslint-disable-next-line
  }, [needAmount, payments, runPay, hasVirman, payCustomerId]);

  // ====== UI ======
  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        background: palette.appBg,
      }}
    >
      {/* HEADER */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: palette.headerBg,
          backdropFilter: "blur(6px)",
          borderBottom: `1px solid ${palette.headerBorder}`,
        }}
      >
        <Row
          align="middle"
          justify="space-between"
          style={{ padding: "8px 12px" }}
        >
          <Col>
            <Space size={12} wrap>
              <Title
                style={{ color: theme === "dark" ? "#fbbf24" : "#374151" }}
                level={4}
              >
                iTable POS
              </Title>
              <Input
                ref={searchRef}
                allowClear
                placeholder="Pretraga: naziv, cijena, kategorija ili šifra"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ width: 340 }}
              />
              {!!query && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Rezultata: {(filteredProducts || []).length}
                </Text>
              )}
              {isLoggedIn ? (
                <Badge
                  status="success"
                  style={{ color: theme === "dark" ? "#fbbf24" : "#374151" }}
                  text={
                    <span>
                      Radnik: <b>{employee?.name}</b>
                    </span>
                  }
                />
              ) : (
                <Badge status="default" text="Nije prijavljen" />
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              {!canFiscal && (
                <Button
                  icon={<BarChartOutlined />}
                  onClick={() => setReportsOpen(true)}
                >
                  Izvještaji
                </Button>
              )}
              {canFiscal && (
                <Button
                  icon={<BarChartOutlined />}
                  onClick={() => setReportsOpen(true)}
                >
                  Manager Report
                </Button>
              )}
              <Button onClick={openOrders}>
                {
                  //add pils like number of orders and change color if there are any
                orders.length > 0 ? (
                  <Badge
                    count={orders.length}
                    size="small"
                    style={{
                      backgroundColor: "#f5222d",
                      boxShadow: "0 0 0 1px #fff",
                    }}
                  />
                ) : null} 
              
                Nenaplaćeno 
              </Button>
              <Button
                icon={theme === "dark" ? <BulbFilled /> : <BulbOutlined />}
                type="text"
                style={{
                  color: theme === "dark" ? "#fbbf24" : "#374151",
                  fontSize: 24,
                }}
                title={
                  theme === "dark" ? "Prebaci na Light" : "Prebaci na Dark"
                }
                onClick={() =>
                  setTheme((t) => (t === "dark" ? "light" : "dark"))
                }
              />
              {isLoggedIn && (
                <Button icon={<LogoutOutlined />} onClick={doLogout} danger>
                  Odjava
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <CategoryBar
          categories={categories as any}
          activeCat={activeCat}
          onPick={setActiveCat}
        />
      </div>

      {/* BODY */}
      <div style={{ height: "calc(100vh - 132px)", padding: 12 }}>
        <Row gutter={12} style={{ height: "100%" }}>
          <Col
            flex="5 1 0"
            style={{
              minWidth: 300,
              paddingTop: 12,
              height: "90vh",
              backgroundColor: palette.leftBg,
              overflowY: "scroll",
              paddingBottom: 150,
              borderTopRightRadius: 18,
            }}
          >
            <ProductGrid
              products={filteredProducts as any}
              onClick={openModifiers}
              palette={palette}
              theme={theme}
            />
          </Col>

          <Col flex="1 1 220px" style={{ minWidth: 220, height: "100%" }}>
            <CartSidebar
              createKitchenOrder={confirmCreateOrder}
              onUpdateNote={onUpdateNote}
              cart={cart as any}
              total={total}
              onInc={inc}
              onDec={dec}
              onRem={rem}
              onPay={openPaymentForCart}
              orderType={orderType}
              onChangeOrderType={setOrderType}
              orderLoading={orderLoading}
              payLoading={payLoading}
            />
          </Col>
        </Row>
      </div>

      {/* MODAL: Poveži uređaj */}
      <Modal
        title="Poveži uređaj (UUID)"
        open={deviceModal}
        closable={false}
        maskClosable={false}
        footer={
          <>
            <Button
              type="link"
              onClick={() => {
                localStorage.setItem("device.isKiosk", "1");
                window.location.reload();
              }}
            >
              Kiosk
            </Button>
            <Button
              type="link"
              onClick={() => {
                localStorage.setItem("device.isTv", "1");
                window.location.reload();
              }}
            >
              TV
            </Button>
            <Button type="primary" onClick={ensureDevice}>
              Poveži
            </Button>
          </>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="secondary">
            Unesi tačan UUID uređaja. API: /pos/device/register
          </Text>
          <Input
            autoFocus
            placeholder="UUID uređaja"
            value={deviceUUID}
            onChange={(e) => setDeviceUUID(e.target.value)}
          />
          <Input
            placeholder="Naziv (opciono)"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
          />
          <Text type="secondary">
            Trenutni: <b>{device?.name || device?.uuid || "—"}</b>
          </Text>
        </Space>
      </Modal>

      {/* OVERLAY: PIN */}
      {!isLoggedIn && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              theme === "dark"
                ? "linear-gradient(135deg, rgba(2,6,23,.95), rgba(15,23,42,.95))"
                : "linear-gradient(135deg, rgba(244,247,252,.95), rgba(232,238,248,.95))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <video autoPlay muted loop className="bg-video">
            <source src="/login-bg.mp4" type="video/mp4" />
          </video>
          <Card
            style={{
              zIndex: 100,
              borderRadius: 20,
              boxShadow: "0 12px 40px rgba(0,0,0,.12)",
            }}
            bodyStyle={{ padding: 20 }}
          >
            <Title level={3} style={{ textAlign: "center", marginBottom: 4 }}>
              Prijava radnika
            </Title>
            <Text
              type="secondary"
              style={{
                display: "block",
                textAlign: "center",
                marginBottom: 14,
              }}
            >
              Uređaj: {device?.name || device?.uuid || "—"}
            </Text>
            <PinPad onSubmit={doLogin} />
          </Card>
        </div>
      )}

      {/* MODAL – modifikatori */}
      <ModifierModal
        open={modOpen}
        productName={modProduct?.name}
        groups={modGroups}
        selections={modSelections}
        setSelections={(updater) => setModSelections((prev) => updater(prev))}
        note={modNote}
        setNote={setModNote}
        onOk={confirmModifiers}
        onCancel={() => setModOpen(false)}
      />

      {/* MODAL – Istorija fiskalnih + storno */}
      <Modal
        title="Istorija fiskalnih računa"
        open={histOpen}
        onCancel={() => setHistOpen(false)}
        footer={null}
        width={720}
      >
        <div style={{ maxHeight: 420, overflowY: "auto" }}>
          {histRows.map((r) => (
            <Card
              key={r.id}
              size="small"
              style={{
                marginBottom: 8,
                borderRadius: 14,
                boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
              }}
            >
              <Row align="middle" justify="space-between">
                <Col>
                  <Space direction="vertical" size={0}>
                    <Text strong>#{r.number || r.id}</Text>
                    <Text type="secondary">{r.created_at || ""}</Text>
                    <Text>Status: {r.status}</Text>
                  </Space>
                </Col>
                <Col>
                  <Space>
                    <Text style={{ fontWeight: 700 }}>
                      {Number(r.total || 0).toFixed(2)} KM
                    </Text>
                    <Button
                      danger
                      disabled={r.status === "voided"}
                      onClick={async () => {
                        try {
                          await voidReceipt(r.id);
                          message.success("Stornirano.");
                          openHistory();
                        } catch {
                          message.error("Storno nije uspjelo.");
                        }
                      }}
                    >
                      Storno
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>
          ))}
        </div>
      </Modal>

      {/* DRAWER – Otvorene narudžbe */}
      <Drawer
        title="Otvorene narudžbe"
        open={ordersOpen}
        onClose={() => setOrdersOpen(false)}
        width={720}
      >
        <div style={{ maxHeight: '90vh', overflowY: "auto" }}>
          {(orders ?? []).map((o) => (
            <Card
              key={o.id}
              size="small"
              style={{
                marginBottom: 8,
                borderRadius: 14,
                boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
              }}
            >
              <Row justify="space-between" align="middle">
                <Col>
                  <Space direction="vertical" size={0}>
                    <Text strong>#{o.number || o.id}</Text>
                    <Text type="secondary">
                      {o.table_label ? `Sto: ${o.table_label} · ` : ""}
                      {o.status}
                    </Text>
                    <Text type="secondary">
                      {new Date(o.created_at).toLocaleString()}
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Space>
                    <Text style={{ fontWeight: 700 }}>
                      {orderTotal(o).toFixed(2)} KM
                    </Text>
                    <Button
                      type="primary"
                      onClick={() => openPaymentForOrder(o)}
                    >
                      Naplati
                    </Button>
                    {
                      //cancel order and set status to cancelled
                    o.status !== "cancelled" && (
                      <Button
                        danger
                        onClick={async () => {
                          try {
                            await pushOrderStatusById(
                              Number(o.id),
                              "cancelled"
                            );
                            setOrders((lst) =>
                              (lst || []).filter((x) => x.id !== o.id)
                            );
                            message.success("Narudžba otkazana.");
                          } catch {
                            message.error("Greška pri otkazivanju.");
                          }
                        }}
                      >
                        Otkaži
                      </Button>
                    )}
                  </Space>
                </Col>
              </Row>
              <div style={{ marginTop: 8 }}>
                {(o.items ?? []).map((i: any) => (
                  <div key={i.id} style={{ marginBottom: 6 }}>
                    <div>
                      <strong>{i.product_name}</strong> × {i.quantity} —{" "}
                      {Number(i.unit_price || 0).toFixed(2)} KM
                    </div>
                    {!!i.modifiers?.length && (
                      <ul style={{ margin: "4px 0 0 16px" }}>
                        {i.modifiers.map((m: any, idx: number) => (
                          <li key={idx}>
                            + {m.name}
                            {typeof m.price_delta !== "undefined" &&
                              ` (${Number(m.price_delta || 0).toFixed(2)} KM)`}
                            {m.quantity && m.quantity !== 1
                              ? ` × ${m.quantity}`
                              : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
          {(!orders || orders.length === 0) && (
            <Empty description="Nema otvorenih narudžbi" />
          )}
        </div>
      </Drawer>

      {/* DRAWER – Izvještaji + istorija */}
      {canFiscal ? (
        <Drawer
          title="Izvještaji i istorija"
          open={reportsOpen}
          onClose={() => setReportsOpen(false)}
          width={420}
        >
          <Space direction="vertical" style={{ width: "100%" }}></Space>
        </Drawer>
      ) : (
        <Drawer
          title="Izvještaji i istorija"
          open={reportsOpen}
          onClose={() => setReportsOpen(false)}
          width={420}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button onClick={openHistory}>Istorija fiskalnih računa</Button>
            <Button
              onClick={() => {
                Modal.confirm({
                  title: "Dnevni presjek",
                  content: "Da li sigurno želiš uraditi dnevni presjek?",
                  okText: "Potvrdi",
                  cancelText: "Odustani",
                  onOk: async () => {
                    try {
                      await printXReportLocal();
                      message.success("Dnevni presjek poslan na štampu.");
                    } catch {
                      message.error("Greška pri presjeku.");
                    }
                  },
                });
              }}
            >
              Dnevni presjek
            </Button>
            <Button
              onClick={() => {
                let range: any[] = [];
                Modal.confirm({
                  title: "Periodični izvještaj",
                  content: (
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <DatePicker.RangePicker
                        style={{ width: "100%" }}
                        onChange={(v) => (range = v as any)}
                        showTime
                      />
                    </Space>
                  ),
                  okText: "Štampaj",
                  onOk: async () => {
                    if (!range || range.length !== 2) {
                      message.warning("Odaberi period.");
                      return Promise.reject();
                    }
                    try {
                      await printPeriodicReportLocal(
                        range[0].toDate(),
                        range[1].toDate()
                      );
                      message.success(
                        "Periodični izvještaj poslan na štampu."
                      );
                    } catch {
                      message.error("Greška pri periodičnom izvještaju.");
                    }
                  },
                });
              }}
            >
              Periodični izvještaj
            </Button>
            <Button
              onClick={() => {
                Modal.confirm({
                  title: "Dnevni izvještaj (Z)",
                  content: "Zaključak dana — želiš li sigurno nastaviti?",
                  okText: "Potvrdi",
                  cancelText: "Odustani",
                  onOk: async () => {
                    try {
                      await printZReportLocal();
                      message.success("Dnevni izvještaj poslan na štampu.");
                    } catch {
                      message.error("Greška pri izvještaju.");
                    }
                  },
                });
              }}
            >
              Dnevni izvještaj
            </Button>
            <Button
              onClick={() => {
                Modal.confirm({
                  title: "Unos depozita",
                  content: (
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <InputNumber
                        id="depositAmt"
                        min={0}
                        style={{ width: "100%" }}
                        placeholder="Iznos (KM)"
                      />
                      <Input
                        id="depositNote"
                        placeholder="Napomena (opciono)"
                      />
                    </Space>
                  ),
                  okText: "Sačuvaj",
                  onOk: async () => {
                    const amt = Number(
                      (document.getElementById("depositAmt") as any)?.value || 0
                    );
                    if (amt <= 0) {
                      message.warning("Unesi iznos > 0.");
                      return Promise.reject();
                    }
                    try {
                      await cashInLocal("Gotovina", amt);
                      message.success(
                        "Depozit (unos novca) poslan na kasu."
                      );
                    } catch {
                      message.error("Greška pri unosu depozita.");
                    }
                  },
                });
              }}
            >
              Unesi depozit (početno stanje)
            </Button>
            <Button
              onClick={() => {
                Modal.confirm({
                  title: "Isplata iz kase",
                  content: (
                    <InputNumber
                      id="payoutAmt"
                      min={0}
                      style={{ width: "100%" }}
                      placeholder="Iznos (KM)"
                    />
                  ),
                  okText: "Potvrdi",
                  onOk: async () => {
                    const amt = Number(
                      (document.getElementById("payoutAmt") as any)?.value || 0
                    );
                    if (amt <= 0) {
                      message.warning("Unesi iznos > 0.");
                      return Promise.reject();
                    }
                    try {
                      await cashOutLocal("Gotovina", amt);
                      message.success("Isplata poslana.");
                    } catch {
                      message.error("Greška pri isplati.");
                    }
                  },
                });
              }}
            >
              Isplata (povrat novca)
            </Button>
          </Space>
        </Drawer>
      )}

      {/* MODAL – Plaćanje (split uplate + opcioni klijent za virman) */}
      <Modal
        title="Plaćanje"
        open={payOpen}
        onCancel={() => setPayOpen(false)}
        okText="Naplati"
        onOk={confirmAndRunPay}
        okButtonProps={{ loading: payLoading }}
        width={560}
      >
        <div style={{ marginBottom: 8 }}>
          Ukupno: <b>{needAmount.toFixed(2)} KM</b>
        </div>

        {/* Ako je u splitu prisutan Virman → traži klijenta */}
        {hasVirman && (
          <div
            style={{
              padding: 10,
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              marginBottom: 10,
              background: "#fff",
            }}
          >
            <div style={{ marginBottom: 6, fontWeight: 600 }}>
              Klijent (firma):
            </div>
            <Select
              showSearch
              allowClear
              style={{ width: "100%" }}
              placeholder="Odaberi klijenta"
              options={customerOpts}
              value={payCustomerId}
              onChange={(v) => setPayCustomerId(v)}
              filterOption={(i, o) =>
                (o?.label as string).toLowerCase().includes(i.toLowerCase())
              }
            />
          </div>
        )}

        {/* Klijent dao + kusur */}
        <div
          style={{
            padding: 10,
            border: "1px dashed #e5e7eb",
            borderRadius: 10,
            marginBottom: 8,
          }}
        >
          <Space
            align="center"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <Text>Klijent dao (gotovina):</Text>
            <InputNumber
              min={0}
              style={{ width: 160 }}
              value={cashGiven}
              onChange={(val) => {
                const v = Number(val || 0);
                setCashGiven(v);
                setPayments((prev) => {
                  const idx = prev.findIndex((p) => p.type === "Gotovina");
                  const next = [...prev];
                  if (idx === -1)
                    next.unshift({ type: "Gotovina", amount: needAmount });
                  else next[idx] = { ...prev[idx], amount: needAmount };
                  return next;
                });
              }}
            />
          </Space>
          <Divider style={{ margin: "10px 0" }} />
          {cashGiven >= needAmount ? (
            <Text strong style={{ color: "#16a34a" }}>
              Kusur: {(cashGiven - needAmount).toFixed(2)} KM
            </Text>
          ) : (
            <Text strong style={{ color: "#b91c1c" }}>
              Preostalo: {(needAmount - cashGiven).toFixed(2)} KM
            </Text>
          )}
        </div>

        {/* Split uplate (postojeća logika sa Select + iznos) */}
        <Space direction="vertical" style={{ width: "100%" }}>
          {payments.map((p, idx) => (
            <Space
              key={idx}
              style={{ display: "flex", justifyContent: "space-between" }}
              align="center"
            >
              <Select
                style={{ width: 160 }}
                value={p.type}
                options={payTypes.map((t) => ({ label: t, value: t }))}
                onChange={(val) => {
                  const next = [...payments];
                  next[idx] = { ...p, type: val };
                  setPayments(next);
                }}
              />
              <InputNumber
                style={{ width: 160 }}
                min={0}
                value={p.amount}
                onChange={(val) => {
                  const next = [...payments];
                  next[idx] = { ...p, amount: Number(val || 0) };
                  setPayments(next);
                  if (p.type === "Gotovina") setCashGiven(Number(val || 0));
                }}
              />
              <Button
                danger
                onClick={() => {
                  const next = payments.filter((_, i) => i !== idx);
                  setPayments(
                    next.length
                      ? next
                      : [{ type: "Gotovina", amount: needAmount }]
                  );
                }}
              >
                Ukloni
              </Button>
            </Space>
          ))}
          <Space>
            <Button
              onClick={() =>
                setPayments([...payments, { type: "Gotovina", amount: 0 }])
              }
            >
              Dodaj uplatu
            </Button>
            <div style={{ marginLeft: "auto" }}>
              Uplate: <b>{sumPayments.toFixed(2)} KM</b>
            </div>
          </Space>
        </Space>
      </Modal>
<Modal
  open={monriOpen}
  footer={null}
  closable={false}
  maskClosable={false}
  width={560}
  styles={{ body: { textAlign: "center", zIndex: 9999999 } as any }}
>
  <div style={{ padding: 8 }}>
    <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>
      {monriStatus?.text || "Obrada plaćanja…"}
    </div>
    {monriStatus?.code && (
      <div style={{ opacity: 0.8, fontSize: 13 }}>
        Status: #{monriStatus.code}
      </div>
    )}
    {monriStatus?.invoice && (
      <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
        Invoice: {monriStatus.invoice}
      </div>
    )}
    <div style={{ opacity: 0.75, fontSize: 13, marginTop: 8 }}>
      Ne uklanjajte karticu niti prekidajte proces.
    </div>
  </div>
</Modal>

      <DeviceSettingsModal
        open={open}
        onClose={() => setOpen(false)}
        canEdit={canEdit}
      />
    </div>
  );
}
